/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Technical Disclaimer Page
 *
 * Data-dense appendix showing:
 * - Engine assumptions (what the AI assumed)
 * - Calculated loads (peak heat loss, flow temps, etc.)
 * - Legal disclaimers and caveats
 *
 * Design philosophy:
 * - Dense 3-column table format
 * - Small font (8pt) for data-heavy feel
 * - Shows the math behind the simple graphics
 * - Adds professional weight and legal protection
 */

import type { TechnicalDisclaimerData } from '@core/presentationPrint';

interface TechnicalDisclaimerPageProps {
  data: TechnicalDisclaimerData;
}

/**
 * Main Technical Disclaimer Page component
 */
export function TechnicalDisclaimerPage({ data }: TechnicalDisclaimerPageProps) {
  return (
    <div className="dp-page dp-technical-disclaimer-page">
      <div className="dp-disclaimer-header">
        <h2>{data.title}</h2>
        <p className="dp-disclaimer-subtitle">
          This page provides transparency on the assumptions and calculations used in this recommendation.
          All values are indicative and subject to verification during full survey.
        </p>
      </div>

      {/* Engine Assumptions Section */}
      <section className="dp-disclaimer-section">
        <h3 className="dp-disclaimer-section__title">Engine Assumptions</h3>
        <p className="dp-disclaimer-section__intro">
          Where specific data was not available, the following assumptions were used based on typical values for similar properties:
        </p>
        <table className="dp-disclaimer-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Assumed Value</th>
              <th>Source/Basis</th>
            </tr>
          </thead>
          <tbody>
            {data.engineAssumptions.map((row, idx) => (
              <tr key={idx}>
                <td>{row.parameter}</td>
                <td>{row.assumed}</td>
                <td>{row.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Calculated Loads Section */}
      <section className="dp-disclaimer-section">
        <h3 className="dp-disclaimer-section__title">Calculated Load Values</h3>
        <p className="dp-disclaimer-section__intro">
          The following values were calculated by the recommendation engine based on property characteristics and assumptions:
        </p>
        <table className="dp-disclaimer-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            {data.calculatedLoads.map((row, idx) => (
              <tr key={idx}>
                <td>{row.metric}</td>
                <td className="dp-value-cell">{row.value}</td>
                <td>{row.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Disclaimers Section */}
      <section className="dp-disclaimer-section">
        <h3 className="dp-disclaimer-section__title">Important Disclaimers</h3>
        <ul className="dp-disclaimer-list">
          {data.disclaimers.map((disclaimer, idx) => (
            <li key={idx}>{disclaimer}</li>
          ))}
        </ul>
      </section>

      {/* Additional Legal Notice */}
      <div className="dp-legal-notice">
        <p>
          <strong>Professional Installation Required:</strong> All recommendations assume professional 
          installation by qualified heating engineers. DIY installation is not recommended and may void 
          warranties or violate building regulations.
        </p>
        <p>
          <strong>Survey Requirement:</strong> This report is indicative only. A full in-home survey is 
          required before confirming any installation. Final specifications, costs, and performance may 
          differ based on survey findings.
        </p>
      </div>

      {/* Styling for this page */}
      <style>{`
        .dp-technical-disclaimer-page {
          padding: 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 9pt;
        }

        .dp-disclaimer-header {
          margin-bottom: 24px;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 12px;
        }

        .dp-disclaimer-header h2 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 6px;
          color: #1e293b;
        }

        .dp-disclaimer-subtitle {
          font-size: 10pt;
          color: #64748b;
          line-height: 1.4;
        }

        .dp-disclaimer-section {
          margin-bottom: 20px;
          page-break-inside: avoid;
        }

        .dp-disclaimer-section__title {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 6px;
          color: #0f172a;
          border-bottom: 1px solid #cbd5e1;
          padding-bottom: 4px;
        }

        .dp-disclaimer-section__intro {
          font-size: 9pt;
          color: #475569;
          margin-bottom: 10px;
          line-height: 1.4;
        }

        .dp-disclaimer-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 16px;
          font-size: 8pt;
        }

        .dp-disclaimer-table thead {
          background: #f1f5f9;
        }

        .dp-disclaimer-table th {
          text-align: left;
          padding: 6px 8px;
          font-weight: 600;
          color: #0f172a;
          border: 1px solid #cbd5e1;
        }

        .dp-disclaimer-table td {
          padding: 5px 8px;
          border: 1px solid #e2e8f0;
          color: #334155;
        }

        .dp-disclaimer-table tbody tr:nth-child(even) {
          background: #f8fafc;
        }

        .dp-value-cell {
          font-weight: 600;
          color: #0284c7;
        }

        .dp-disclaimer-list {
          list-style: disc;
          padding-left: 20px;
          font-size: 9pt;
          color: #475569;
          line-height: 1.5;
        }

        .dp-disclaimer-list li {
          margin-bottom: 6px;
        }

        .dp-legal-notice {
          margin-top: 24px;
          padding: 12px;
          background: #fef2f2;
          border: 1px solid #fca5a5;
          border-radius: 6px;
        }

        .dp-legal-notice p {
          font-size: 8pt;
          color: #991b1b;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .dp-legal-notice p:last-child {
          margin-bottom: 0;
        }

        .dp-legal-notice strong {
          font-weight: 700;
        }

        /* Print styles */
        @media print {
          .dp-technical-disclaimer-page {
            page-break-before: always;
            font-size: 8pt;
          }

          .dp-disclaimer-section {
            page-break-inside: avoid;
          }

          .dp-disclaimer-table {
            font-size: 7pt;
          }

          .dp-legal-notice {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}

export default TechnicalDisclaimerPage;
